import { useCallback, useEffect, useState } from 'react';
import {
  UtmAnalyticsResponse,
  CampaignMetric,
  GroupedLeadConversion,
  QuoteDetail,
  AnalyticsActiveTab
} from '../types';
import * as analyticsService from '../services/analytics.service';
import { withAuthRetry } from '../utils/withAuthRetry';

interface UseUtmAnalyticsProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
  canViewAnalytics: boolean;
}

export interface DateRange {
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
}

function getDefaultDateRange(): DateRange {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);

  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0]
  };
}

export function useUtmAnalytics({
  accessToken,
  refreshToken,
  attemptRefresh,
  canViewAnalytics
}: UseUtmAnalyticsProps) {
  // Analytics data state
  const [analytics, setAnalytics] = useState<UtmAnalyticsResponse | null>(null);
  const [conversions, setConversions] = useState<GroupedLeadConversion[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [conversionsLoading, setConversionsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<AnalyticsActiveTab>('utm');
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange());

  // Filter state for conversions
  const [campaignFilter, setCampaignFilter] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<string>('');

  // Quotes modal state
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const [emailQuotes, setEmailQuotes] = useState<QuoteDetail[]>([]);
  const [quotesLoading, setQuotesLoading] = useState(false);

  // Fetch main analytics data
  const fetchAnalytics = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken || !canViewAnalytics) {
      return { success: false, error: 'Não autorizado' };
    }

    setLoading(true);

    try {
      const data = await withAuthRetry(
        () => analyticsService.fetchAnalytics(accessToken!, dateRange),
        refreshToken,
        attemptRefresh
      );
      setAnalytics(data);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro inesperado ao buscar analytics'
      };
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, canViewAnalytics, dateRange]);

  // Fetch grouped conversions data (deduplicated by email)
  const fetchConversions = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken || !canViewAnalytics) {
      return { success: false, error: 'Não autorizado' };
    }

    setConversionsLoading(true);

    try {
      const data = await withAuthRetry(
        () => analyticsService.fetchGroupedConversions(accessToken!, {
          ...dateRange,
          utmCampaign: campaignFilter || undefined,
          utmSource: sourceFilter || undefined
        }),
        refreshToken,
        attemptRefresh
      );
      setConversions(data);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro inesperado ao buscar conversões'
      };
    } finally {
      setConversionsLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, canViewAnalytics, dateRange, campaignFilter, sourceFilter]);

  // Fetch quotes for a specific email
  const fetchQuotesForEmail = useCallback(async (email: string): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken) {
      return { success: false, error: 'Não autenticado' };
    }

    setQuotesLoading(true);
    setSelectedEmail(email);

    try {
      const data = await withAuthRetry(
        () => analyticsService.fetchQuotesByEmail(accessToken!, email),
        refreshToken,
        attemptRefresh
      );
      setEmailQuotes(data);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro ao buscar orçamentos'
      };
    } finally {
      setQuotesLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh]);

  // Close quotes modal
  const closeQuotesModal = useCallback(() => {
    setSelectedEmail(null);
    setEmailQuotes([]);
  }, []);

  // Export campaigns CSV
  const exportCampaignsCsv = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken) {
      return { success: false, error: 'Não autenticado' };
    }

    try {
      const blob = await analyticsService.exportCampaignsCsv(accessToken, dateRange);
      analyticsService.downloadBlob(blob, `campanhas-utm-${dateRange.startDate}-${dateRange.endDate}.csv`);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: 'Erro ao exportar campanhas'
      };
    }
  }, [accessToken, dateRange]);

  // Export conversions CSV
  const exportConversionsCsv = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken) {
      return { success: false, error: 'Não autenticado' };
    }

    try {
      const blob = await analyticsService.exportConversionsCsv(accessToken, {
        ...dateRange,
        utmCampaign: campaignFilter || undefined,
        utmSource: sourceFilter || undefined
      });
      analyticsService.downloadBlob(blob, `conversoes-${dateRange.startDate}-${dateRange.endDate}.csv`);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: 'Erro ao exportar conversões'
      };
    }
  }, [accessToken, dateRange, campaignFilter, sourceFilter]);

  // Get unique campaigns from analytics data for filter dropdown
  const availableCampaigns = analytics?.campaignMetrics?.map((m: CampaignMetric) => m.campaign) || [];
  const availableSources = analytics?.sourceMetrics?.map((m) => m.source) || [];

  // Auto-fetch when date range changes
  useEffect(() => {
    if (accessToken && canViewAnalytics) {
      fetchAnalytics();
    }
  }, [accessToken, canViewAnalytics, dateRange]);

  // Fetch conversions when filters change
  // Note: No longer auto-fetching based on activeTab since it's now controlled by AnalyticsPage sub-tabs
  useEffect(() => {
    if (accessToken && canViewAnalytics && activeTab === 'utm') {
      fetchConversions();
    }
  }, [accessToken, canViewAnalytics, activeTab, dateRange, campaignFilter, sourceFilter]);

  return {
    // Data
    analytics,
    conversions,

    // Loading states
    loading,
    conversionsLoading,

    // UI state
    activeTab,
    setActiveTab,
    dateRange,
    setDateRange,

    // Filters
    campaignFilter,
    setCampaignFilter,
    sourceFilter,
    setSourceFilter,
    availableCampaigns,
    availableSources,

    // Quotes modal
    selectedEmail,
    emailQuotes,
    quotesLoading,
    fetchQuotesForEmail,
    closeQuotesModal,

    // Actions
    fetchAnalytics,
    fetchConversions,
    exportCampaignsCsv,
    exportConversionsCsv
  };
}
